import type { LoginResult } from '@/types/user'
import { clearToken, getToken, setToken } from '@/utils/auth'

const DONGCHA_ACCOUNT_UUID_KEY = 'qx_dongcha_account_uuid'
const DONGCHA_RELOAD_READY_KEY = 'qx_dongcha_entry_ready_once'
const WECOM_RELOGIN_ORIGIN_KEY = 'qx_wecom_relogin_origin'

const ACCOUNT_SCOPED_KEYS = [
  'chat-store',
  'chat-store-pending',
  'bill-store',
  'bill-detail-store',
  'qx-draft-platform-selection',
  'bill-platform-store',
  'messageBubbleLastShownMessageId',
  'messageCenterReadSync',
  'user-store',
  'qx-wecom-after-add-pending',
  'qx-wecom-after-add-due-time',
  'qx-pending-home-tab',
]

const getStorageValue = (storage: Storage, key: string) => {
  try {
    return storage.getItem(key) || ''
  } catch {
    return ''
  }
}

export const hasPersistedDongchaAccountState = () => {
  try {
    return ACCOUNT_SCOPED_KEYS.some(
      key => localStorage.getItem(key) !== null || sessionStorage.getItem(key) !== null,
    )
  } catch {
    return false
  }
}

export const getDongchaAccountMarkerUuid = () => {
  try {
    return localStorage.getItem(DONGCHA_ACCOUNT_UUID_KEY) || ''
  } catch {
    return ''
  }
}

export const getCurrentTokenAccountUuid = async () => {
  const token = await getToken()
  if (!token) return ''
  try {
    const payload = token.split('.')[1]
    if (!payload) return ''
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    const claims = JSON.parse(atob(padded)) as { uuid?: unknown }
    return typeof claims.uuid === 'string' ? claims.uuid : ''
  } catch {
    return ''
  }
}

export const rememberWeComReloginOrigin = (bindingToken: string, uuid: string) => {
  if (!bindingToken || !uuid) return
  try {
    sessionStorage.setItem(WECOM_RELOGIN_ORIGIN_KEY, JSON.stringify({ bindingToken, uuid }))
  } catch {
    // Account-scoped storage cleanup remains the fallback when session storage is unavailable.
  }
}

export const consumeWeComReloginOrigin = (bindingToken: string) => {
  if (!bindingToken) return ''
  try {
    const rawValue = sessionStorage.getItem(WECOM_RELOGIN_ORIGIN_KEY)
    sessionStorage.removeItem(WECOM_RELOGIN_ORIGIN_KEY)
    if (!rawValue) return ''
    const value = JSON.parse(rawValue) as { bindingToken?: unknown, uuid?: unknown }
    return value.bindingToken === bindingToken && typeof value.uuid === 'string'
      ? value.uuid
      : ''
  } catch {
    return ''
  }
}

const clearPersistedAccountState = () => {
  try {
    ACCOUNT_SCOPED_KEYS.forEach((key) => {
      localStorage.removeItem(key)
      sessionStorage.removeItem(key)
    })
  } catch {
    // A hard reload follows account changes; unavailable storage needs no fallback mutation.
  }
}

export const applyDongchaLoginResult = (
  result: LoginResult,
  previousInMemoryUuid?: string,
  hadPersistedAccountState = hasPersistedDongchaAccountState(),
) => {
  if (!result?.token || !result.uuid) {
    throw new Error('洞察登录响应缺少账号标识')
  }

  const previousMarker = getStorageValue(localStorage, DONGCHA_ACCOUNT_UUID_KEY)
  const previousUuid = previousInMemoryUuid || previousMarker
  const accountChanged = previousUuid
    ? previousUuid !== result.uuid
    : hadPersistedAccountState

  if (accountChanged) {
    clearToken()
    clearPersistedAccountState()
  }
  setToken(result.token)
  try {
    localStorage.setItem(DONGCHA_ACCOUNT_UUID_KEY, result.uuid)
  } catch {
    // The access token remains the authoritative identity for the current page.
  }
  return accountChanged
}

export const markDongchaReloadReady = () => {
  try {
    sessionStorage.setItem(DONGCHA_RELOAD_READY_KEY, '1')
  } catch {
    // Without session storage the router will re-enter the Dongcha login page.
  }
}

export const consumeDongchaReloadReady = () => {
  try {
    const ready = sessionStorage.getItem(DONGCHA_RELOAD_READY_KEY) === '1'
    sessionStorage.removeItem(DONGCHA_RELOAD_READY_KEY)
    return ready
  } catch {
    return false
  }
}
